import * as Select from '@radix-ui/react-select';
import clsx from 'clsx';
import Image from 'next/image';
import { Control, FieldValues, Path } from 'react-hook-form';
import { FormField, FormItem, FormMessage } from '../atoms/form';

type Option = {
  label: string;
  value: string;
};

interface CustomSelectProps {
  options: Option[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export default function CustomSelect({
  options,
  value,
  onChange,
  placeholder = 'Selecione...',
  className,
}: CustomSelectProps) {
  return (
    <Select.Root value={value} onValueChange={onChange}>
      <Select.Trigger
        aria-label={placeholder}
        className={clsx(
          'inline-flex items-center data-[placeholder]:text-gray-600 justify-between rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400',
          className
        )}
      >
        <Select.Value placeholder={placeholder} />
        <Select.Icon>
          <Image
            src="/icons/arrow-down-circle.png"
            alt="Seta para baixo"
            width={24}
            height={24}
          />
        </Select.Icon>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content className="bg-white border border-gray-300 rounded-md shadow-md z-50">
          <Select.Viewport className="p-1">
            {options.map((option) => (
              <Select.Item
                key={option.value}
                value={option.value}
                className="font-bold cursor-pointer p-4 text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none rounded"
              >
                <Select.ItemText>{option.label}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}

interface FormattedCustomSelectProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  options: Option[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function FormattedCustomSelect<TFieldValues extends FieldValues>({
  control,
  name,
  options,
  placeholder = 'Selecione...',
  className,
}: FormattedCustomSelectProps<TFieldValues>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex-1">
          <Select.Root
            key={field.value ?? 'empty'}
            value={field.value}
            onValueChange={field.onChange}
          >
            <Select.Trigger
              aria-label={placeholder}
              className={clsx(
                'w-full inline-flex items-center data-[placeholder]:text-gray-600 justify-between rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400',
                `${field.value ? 'bg-[#E8F0FE]' : ''}`,
                className
              )}
            >
              <Select.Value placeholder={placeholder} />
              <Select.Icon>
                <Image
                  src="/icons/arrow-down-circle.png"
                  alt="Seta para baixo"
                  width={24}
                  height={24}
                />
              </Select.Icon>
            </Select.Trigger>

            <Select.Portal>
              <Select.Content className="bg-white border border-gray-300 rounded-md shadow-md z-50">
                <Select.Viewport className="p-1">
                  {options.map((option) => (
                    <Select.Item
                      key={option.value}
                      value={option.value}
                      className="font-bold cursor-pointer p-4 text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none rounded"
                    >
                      <Select.ItemText>{option.label}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
